import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { Observable, map } from 'rxjs';
import { Environment } from 'src/environments/environment.model';

import { signalStoreCustomerRequestOptions } from '../../interceptors/signal-store-customer-context';
import {
  BackendDiseaseType,
  BackendSprayType,
  CreateSprayApplicationRequest,
  SprayApplicationHistoryApiResponse,
  SprayApplicationHistoryCropField,
  SprayCocktail,
  SprayCocktailApiResponse,
} from '../models/spray-application.models';

@Injectable({
  providedIn: 'root',
})
export class SprayApplicationService {
  private readonly http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);
  private readonly baseUrl = `${this.env.APIM_BASE_URL}/arable-advice-service`;

  getSprayCocktails(
    applicationDate: Date,
    disease: BackendDiseaseType,
    sprayType: BackendSprayType,
    customerId?: number,
  ): Observable<SprayCocktail[]> {
    const url = `${this.baseUrl}/spray-cocktails`;

    return this.http
      .get<SprayCocktailApiResponse>(url, {
        params: {
          applicationDate: applicationDate.toISOString(),
          disease,
          sprayType,
        },
        ...signalStoreCustomerRequestOptions(customerId),
      })
      .pipe(map((response) => response.sprayCocktails));
  }

  getSprayApplications(
    cropFieldIds: number[],
    disease: BackendDiseaseType,
    customerId?: number,
  ): Observable<SprayApplicationHistoryCropField[]> {
    const url = `${this.baseUrl}/spray-applications`;

    return this.http
      .get<SprayApplicationHistoryApiResponse>(url, {
        params: {
          cropFieldIds: cropFieldIds.map((cropFieldId) => cropFieldId.toString()),
          disease,
        },
        ...signalStoreCustomerRequestOptions(customerId),
      })
      .pipe(
        map((response) =>
          response.cropFields.map((cropField) => ({
            ...cropField,
            sprayApplications: cropField.sprayApplications.map((sprayApplication) => ({
              ...sprayApplication,
              applicationDate: new Date(sprayApplication.applicationDate),
            })),
          })),
        ),
      );
  }

  createSprayApplications(request: CreateSprayApplicationRequest, customerId?: number): Observable<void> {
    const url = `${this.baseUrl}/spray-applications`;

    return this.http.post<void>(url, request, signalStoreCustomerRequestOptions(customerId));
  }

  deleteSprayApplication(cropFieldId: number, sprayApplicationId: string, customerId?: number): Observable<void> {
    const url = `${this.baseUrl}/spray-applications/${cropFieldId}/${sprayApplicationId}`;

    return this.http.delete<void>(url, signalStoreCustomerRequestOptions(customerId));
  }
}
